import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ServicoDaAba } from './escopo-da-tipologia';

/**
 * A aba foi ligada à tipologia: marcar o que ela orça?
 *
 * Nada entra sozinho — foi exatamente o "atrelado sem eu escolher" que a escolha por
 * tipologia veio acabar. A tela mostra a lista já marcada, porque o comum é aceitar, e quem
 * confere desmarca o que a aba orçou por engano.
 */
export function MarcarDaPlanilha({
  open,
  onOpenChange,
  aba,
  tipologia,
  servicos,
  salvando,
  onConfirmar,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  aba: string;
  tipologia: string;
  servicos: readonly ServicoDaAba[];
  salvando: boolean;
  onConfirmar: (chaves: readonly string[]) => void;
}) {
  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    if (open) setMarcados(new Set(servicos.map((item) => item.servico.key)));
    // Só na abertura, pelo mesmo motivo do painel de escolha.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Marcar os serviços da aba?</DialogTitle>
          <DialogDescription>
            A aba “{aba}” orça {servicos.length} {servicos.length === 1 ? 'serviço' : 'serviços'}{' '}
            que {tipologia} ainda não usa. Confira e marque os que valem para esta tipologia.
          </DialogDescription>
        </DialogHeader>

        <ul className="flex max-h-80 flex-col gap-0.5 overflow-y-auto">
          {servicos.map(({ disciplina, servico }) => (
            <li key={servico.key}>
              <label className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-1 text-sm hover:bg-(--color-surface-muted)">
                <input
                  type="checkbox"
                  className="size-4 shrink-0"
                  checked={marcados.has(servico.key)}
                  onChange={(event) =>
                    setMarcados((atual) => {
                      const proximo = new Set(atual);
                      if (event.target.checked) proximo.add(servico.key);
                      else proximo.delete(servico.key);
                      return proximo;
                    })
                  }
                />
                <span className="min-w-0 flex-1 truncate">{servico.name}</span>
                <span className="text-xs text-(--color-content-muted)">{disciplina}</span>
              </label>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Agora não
          </Button>
          <Button
            variant="primary"
            disabled={salvando || marcados.size === 0}
            onClick={() => onConfirmar([...marcados])}
          >
            {salvando
              ? 'Marcando…'
              : `Marcar ${String(marcados.size)} ${marcados.size === 1 ? 'serviço' : 'serviços'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
